import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import vtkImageMarchingCubes from '@kitware/vtk.js/Filters/General/ImageMarchingCubes';
import type { LabelmapBinding } from '@/src/segmentation/model';
import { maskScalars } from '@/src/segmentation/model';
import { extentSize } from '@/src/segmentation/geometry';
import { SEGMENT_VALUE } from '@/src/segmentation/masks/labelValue';
import type { Vector3 } from '@kitware/vtk.js/types';

/** Display-only surface. Neither the mask nor source grid is modified. */
export function buildSegmentationSurface(parent: vtkImageData, binding: LabelmapBinding) {
  const [nx, ny, nz] = extentSize(binding.extent);
  const dims: Vector3 = [nx + 2, ny + 2, nz + 2];
  const values = new Uint8Array(dims[0] * dims[1] * dims[2]);
  const source = maskScalars(binding.image);
  // One background sample on every side closes single-slice/edge masks too.
  for (let z = 0; z < nz; z++) for (let y = 0; y < ny; y++) {
    const start = (z * ny + y) * nx;
    values.set(source.subarray(start, start + nx), ((z + 1) * dims[1] + y + 1) * dims[0] + 1);
  }
  const image = vtkImageData.newInstance();
  image.setDimensions(dims); image.setOrigin([binding.extent[0] - 1, binding.extent[2] - 1, binding.extent[4] - 1]);
  const scalars = vtkDataArray.newInstance({ numberOfComponents: 1, values });
  image.getPointData().setScalars(scalars);
  const cubes = vtkImageMarchingCubes.newInstance({ contourValue: SEGMENT_VALUE / 2, computeNormals: true, mergePoints: true });
  cubes.setInputData(image); cubes.update();
  const poly = cubes.getOutputData(); const points = poly.getPoints().getData();
  // vtk.js 37 marching cubes uses origin/spacing but ignores direction.
  // Generate in absolute IJK and apply the source's complete affine once.
  for (let i = 0; i < points.length; i += 3) {
    const world = parent.indexToWorld([points[i], points[i + 1], points[i + 2]]);
    points[i] = world[0]; points[i + 1] = world[1]; points[i + 2] = world[2];
  }
  poly.getPoints().modified();
  const normals = poly.getPointData().getNormals();
  if (normals) {
    const n = normals.getData(); const d = parent.getDirection(); const s = parent.getSpacing();
    for (let i = 0; i < n.length; i += 3) {
      const x = n[i] / s[0]; const y = n[i + 1] / s[1]; const z = n[i + 2] / s[2];
      const world = [d[0] * x + d[3] * y + d[6] * z, d[1] * x + d[4] * y + d[7] * z, d[2] * x + d[5] * y + d[8] * z];
      const length = Math.hypot(...world) || 1;
      world.forEach((value, axis) => { n[i + axis] = value / length; });
    }
    normals.modified();
  }
  poly.modified(); cubes.delete(); image.delete(); scalars.delete();
  return poly;
}
